import { type App, computed, type ComputedRef, type Ref, ref, watch } from 'vue'
import { type AppTranslator } from '@/i18n'

import {
    type AutologinMutation,
    type AutologinMutationVariables,
    type InitQuery,
    type InitQueryVariables,
} from '@/graphql/api'
import { type MenuPlugin } from '@/plugins/menu.ts'
import { getLocaleForRegion, type RegionPlugin } from '@/plugins/region.ts'
import { trackGtmEvent } from '@/composables/app/gtm/event.ts'
import { type AuthPlugin } from '@/plugins/auth.ts'
import type { Client, UseQueryResponse } from '@urql/vue'
import {
    adaptMutation,
    type SimpleMutationResponse,
} from '@/helpers/mutation-adapter.ts'
import { callUseQuery } from '@/helpers/use-query.ts'
import type { InitialState } from '@/initial-state.ts'
import { type InitPlugin, initKey } from '@/plugins/init/use-init.ts'
import { AutologinDocument } from '@/graphql/api/auth/autologin.mutation.generated.ts'
import { InitDocument } from '@/composables/app/useInit.generated.ts'
import { type BenchFunction } from '@/plugins/bench.ts'

interface InitDependencies {
    translator: AppTranslator
    regionPlugin: RegionPlugin
    menus: MenuPlugin
    auth: AuthPlugin
    isAdmin: ComputedRef<boolean>
    authToken: string | null
    initialState: InitialState
    initialUrl: URL
    bench: BenchFunction
}

export function installInit(
    app: App,
    client: Ref<Client>,
    hadRegionSet: boolean,
    dependencies: InitDependencies
): InitPlugin {
    const plugin = createInit(client, hadRegionSet, dependencies)

    app.provide(initKey, plugin)

    return plugin
}

function createInit(
    client: Ref<Client>,
    hadRegionSet: boolean,
    dependencies: InitDependencies
): InitPlugin {
    const {
        translator,
        regionPlugin,
        menus,
        auth,
        isAdmin,
        authToken,
        initialState,
        bench,
    } = dependencies

    const { setMessages } = translator
    const { regions, regionCode, hasRegionSet } = regionPlugin
    const {
        setTopBar,
        setPreHeader,
        setMainMenu,
        setFooter,
        setPaymentIcons,
        setSocialNetworks,
    } = menus
    const { setUser } = auth
    const autologinMutation: SimpleMutationResponse<
        AutologinMutation,
        AutologinMutationVariables
    > = adaptMutation(client, AutologinDocument)

    const pauseInitQuery = ref(!!authToken)

    const fetchRegionSuggest = !hadRegionSet

    const variables = computed((): InitQueryVariables => {
        return {
            locale: getLocaleForRegion(regionCode.value),
            fetchRegionSuggest: fetchRegionSuggest,
            isConsultant: isAdmin.value,
        }
    })

    const benchmark = bench('Query init')
    const initQueryResponse: UseQueryResponse<InitQuery> = callUseQuery(
        {
            variables,
            pause: pauseInitQuery,
            query: InitDocument,
        },
        client
    )

    const { operation, data, error } = initQueryResponse

    const apiState = initialState.api
    const operationState = apiState[operation.value?.key || '']
    const initData = ref<InitQuery | undefined>(undefined)

    async function processInitData(data: InitQuery): Promise<void> {
        // processTranslations(data.language?.translations || [], translator, ref(region))
        setTopBar(data.topBar)
        setPreHeader(data.preHeader)
        setMainMenu(data.mainMenu)
        setFooter(data.footer)
        setPaymentIcons(data.paymentIcons)
        setSocialNetworks(data.socialNetworks)
        setUser(data.me)
        data.regions.forEach((item) => {
            regions.value[item.code] = item
        })
        if (data.language) {
            // За бъдещите поколения
            // ДА НЕ СЕ МЕСТИ НАД РИНГ БИЛДЪРА
            // ЧЕ СТАВА МНОГО ЛОШО
            // ЗА ОСТАНАЛИТЕ, НЕ Е СЪВСЕМ СИГУРНО
            await setMessages(data.language.locale, data.language.translations)
        }
    }

    watch(data, async (data) => {
        if (!data) {
            return
        }

        // This watcher will set VAT/Rates a second time during INIT in a browser
        // Nothing we can do, but we NEED to set VAT/Rates in the promises
        // Due to the fact that SSR will not react to this
        await processInitData(data)
    })

    const showRegionModal = computed(() => {
        if (hasRegionSet.value) {
            return false
        }

        const regionSuggest = initData.value?.regionSuggest

        if (!regionSuggest) {
            return false
        }

        return regionSuggest.code !== regionCode.value
    })

    trackGtmEvent('site_version', { 'version': 'v2' })

    const isInitialized = ref(false)

    const waitForInit = async (): Promise<void> => {
        let processed = false
        if (operationState?.data) {
            initData.value = JSON.parse(operationState.data) as InitQuery

            if (initData.value) {
                await processInitData(initData.value)
            }

            processed = true
        }

        if (authToken) {
            const autoLoginBench = bench('Mutation AutoLogin')
            const response = await autologinMutation.executeMutation({
                authToken: authToken,
                isConsultant: isAdmin.value,
            })
            autoLoginBench.end('END')

            if (
                response.data?.autologin.__typename === 'LoginResponseSuccess'
            ) {
                setUser(response.data?.autologin.user)
            }

            // TODO remove logic from RegionModal and uncomment this
            // after teleport/suspense/navigation bug is fixed
            // https://github.com/vuejs/core/issues/7966
            // authToken.value = null

            if (QA || import.meta.env.SSR) {
                benchmark.restart()
            }
        }

        pauseInitQuery.value = false

        await initQueryResponse
        if (QA || import.meta.env.SSR) {
            benchmark.end('END')
        }

        initData.value = data.value

        if (!processed && data.value) {
            await processInitData(data.value)
        }

        isInitialized.value = true

        if (QA || import.meta.env.SSR) {
            benchmark.end('InitQuery Data Processed')
        }
    }

    const promise = waitForInit()

    return {
        error,
        initQueryResponse,
        showRegionModal,
        isInitialized,
        promise,
        benchmark,
        enquiryFormContent: computed(() => {
            return Object.fromEntries(
                initData.value?.enquiryFormContents.map((content) => {
                    return [content.slug, content]
                }) ?? []
            )
        }),
    }
}
